import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { withTranslation, Trans } from 'react-i18next';
import {
    HistogramBar,
    Histogram,
    SkeletonItem,
    ThemeDefault,
} from '@paygreen/paygreen-ui';
import { ErrorBoundary, CollapsibleCard } from '../../../components';
import { IsMobile, convertCarbonValue } from '../../../utils/tools';
import { useNodeDimensions } from '../../../utils/hooks';

const DashboardCardCO2Histogram = ({
    t,
    histogramData,
    isLoading,
    endDateValue,
    startDateValue,
}) => {
    const [data, setData] = useState();

    // we base histogram Y axis values on received values
    const YMaxValue = histogramData
        ? Math.max(...histogramData.map((value) => value.carbonEmitted), 0)
        : 0;

    // we convert value and unit on Y axis depending on value
    const { convertedValue: convertedYAxisValue, convertedUnit: yAxisUnit } =
        convertCarbonValue(YMaxValue, true);

    useEffect(() => {
        /**
         * we build data array and complete missing days from API with values at 0
         * @param {integer} values
         */
        const fillMissedDays = (values) => {
            let datesArray = [];
            const endDate = moment(endDateValue, 'DD/MM/YYYY');
            const startDate = moment(startDateValue, 'DD/MM/YYYY');

            while (startDate <= endDate) {
                // for each day we will have the value (absolute value to calculate HistogramBar's height), legendUnit and legendValue converted depending on value and label date
                datesArray.push({
                    value: 0,
                    legendUnit: 'g',
                    legendValue: 0,
                    label: moment(startDate).format('DD/MM/YYYY'),
                });

                if (values && values.length > 0) {
                    values.forEach((value) => {
                        if (
                            moment(value.date).format('DD/MM/YYYY') ===
                            moment(startDate).format('DD/MM/YYYY')
                        ) {
                            const { convertedValue, convertedUnit } =
                                convertCarbonValue(value?.carbonEmitted, true);

                            const valueIndex = datesArray.findIndex(
                                (object) =>
                                    object.label ===
                                    moment(startDate).format('DD/MM/YYYY'),
                            );
                            datesArray[valueIndex] = {
                                ...datesArray[valueIndex],
                                value: value?.carbonEmitted || 0,
                                legendUnit: convertedUnit,
                                legendValue: convertedValue,
                            };
                        }
                    });
                }

                startDate.add(1, 'days');
            }
            return datesArray;
        };

        setData(fillMissedDays(histogramData));
    }, [histogramData, endDateValue, startDateValue]);

    // we create a ref using a special hook to get width from CollapsibleCard component
    const [ref, { width }] = useNodeDimensions();

    const responsiveHistogramBar = {
        blockWidth: IsMobile() ? 'xs' : data && data.length < 15 ? 'md' : 'sm',
        paddingLateral:
            width > ThemeDefault.breakpoint.lg || (data && data.length < 15)
                ? 'sm'
                : 'xs',
    };

    return (
        <ErrorBoundary>
            <CollapsibleCard
                title={t('dashboard.co2HistogramCard.title')}
                blockWidth="none"
                paddingLateral="sm"
                paddingTop="sm"
                paddingBottom="sm"
            >
                <div ref={ref}>
                    {data?.length && YMaxValue !== undefined && (
                        <SkeletonItem
                            blockWidth="auto"
                            colorWab="grey10"
                            radiusSize="lg"
                            marginTop="md"
                            isLoading={isLoading}
                        >
                            <Histogram
                                yaxisUnit={yAxisUnit}
                                yaxisValues={[25, 50, 75]}
                                blockHeight="sm"
                                blockWidth="none"
                                marginTop="md"
                                maxValue={!YMaxValue ? 100 : YMaxValue}
                                yaxisMaxValue={
                                    !YMaxValue ? null : convertedYAxisValue
                                }
                                isRelativeYaxis={true}
                            >
                                {data &&
                                    data.map((sample, index) => (
                                        <HistogramBar
                                            key={index}
                                            value={Number(sample.value)}
                                            legend={
                                                <Trans i18nKey="dashboard.co2HistogramCard.legend">
                                                    {{
                                                        value: sample.legendValue,
                                                    }}
                                                    {{
                                                        unit: sample.legendUnit,
                                                    }}
                                                    le
                                                    {{
                                                        date: sample.label,
                                                    }}
                                                </Trans>
                                            }
                                            isLabelVisible={false}
                                            {...responsiveHistogramBar}
                                        />
                                    ))}
                            </Histogram>
                        </SkeletonItem>
                    )}
                </div>
            </CollapsibleCard>
        </ErrorBoundary>
    );
};

DashboardCardCO2Histogram.propTypes = {
    histogramData: PropTypes.array,
    isLoading: PropTypes.bool,
    endDateValue: PropTypes.string.isRequired,
    startDateValue: PropTypes.string.isRequired,
};

DashboardCardCO2Histogram.defaultProps = {
    isLoading: false,
};

export default withTranslation('common')(DashboardCardCO2Histogram);
